import { formatPeso } from '@tahi/core';
import { useState } from 'react';
import { Link } from 'react-router';
import { logCraving, savePlan, setDayTotal, setQuitOnToday } from '../../../data/commands/quit';
import { useCommandEnv, useUsable } from '../../../data/hooks';
import * as repo from '../../../data/repos/quit';
import { listTiles } from '../../../data/repos/tiles';
import { useQuery } from '../../../data/useQuery';
import { BentoGrid } from '../../../tiles/BentoGrid';
import { visibleTiles } from '../../../tiles/layout';
import { MenuCard, Money, Num } from '../../../ui';
import { Switch } from '../../../ui/Switch';
import { showToast } from '../../../ui/toast';
import { useNow } from '../../../ui/useNow';
import { CountChart } from '../components/CountChart';
import { CravingSheet } from '../components/CravingSheet';
import { DayTotalSheet } from '../components/DayTotalSheet';
import { Milestones } from '../components/Milestones';
import { PlusOneButton } from '../components/PlusOneButton';
import { SupportCard } from '../components/SupportCard';
import { TaperChart } from '../components/TaperChart';
import { copy, errorText } from '../copy';
import { QUIT_BASE, QUIT_DASHBOARD_ID } from '../dashboardDefaults';
import { durationText, shortDate, spokenCount, statusLine } from '../format';
import { useDelay, useLogCigarette, useQuitOverview } from '../hooks';
import { draftFromPlan } from '../planDraft';
import { CARD, H1, HERO_NUM, LABEL, PILL_BTN, QUIET_LINK, SCREEN } from '../styles';

export function QuitDashboard() {
  const q = useQuitOverview();
  const env = useCommandEnv();
  const usable = useUsable();
  const log = useLogCigarette();
  const delay = useDelay();
  const [totalOpen, setTotalOpen] = useState(false);
  const [cravingOpen, setCravingOpen] = useState(false);
  const tick = useNow(cravingOpen ? 1000 : 60_000);
  const { data: rows } = useQuery(['tiles'], (db) => listTiles(db, QUIT_DASHBOARD_ID));
  const { data: todayTiles } = useQuery(['tiles'], (db) => repo.listQuitTodayTiles(db));

  if (!q) {
    return (
      <main className={SCREEN}>
        <p role="status" className="text-muted">
          {copy.loading}
        </p>
      </main>
    );
  }
  const { data, overview: o } = q;
  if (!data.plan || !o) {
    return (
      <main className={SCREEN}>
        <h1 className={H1}>{copy.moduleName}</h1>
        <p className="text-muted">{copy.empty}</p>
        <Link to={`${QUIT_BASE}/plan`} className={`${ACCENT_PILL} self-start`}>
          {copy.setUpPlan}
        </Link>
      </main>
    );
  }
  const plan = data.plan;
  const onToday = (todayTiles ?? []).some((t) => !t.hidden);
  const status = statusLine(o);
  const closeCraving = () => {
    delay.clear();
    setCravingOpen(false);
  };

  return (
    <main className={SCREEN}>
      <header className="flex items-center justify-between gap-3">
        <h1 className={H1}>{copy.moduleName}</h1>
        <Link to={`${QUIT_BASE}/plan`} className={QUIET_LINK}>
          {copy.editPlan}
        </Link>
      </header>

      <div className="flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:items-start">
        <div className="flex flex-col gap-4">
          <section aria-label={spokenCount(o.count, o.limit)} className={CARD}>
            {o.phase === 'smoke_free' && o.minutesSince !== null ? (
              <p className="mb-2 text-lg text-success">
                {copy.smokeFreeFor(durationText(o.minutesSince))}
              </p>
            ) : null}
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className={LABEL}>{copy.cigarettesToday}</p>
                <p className={`${HERO_NUM} mt-1`}>
                  <Num value={o.count} />
                  {o.limit !== null ? (
                    <span className="ml-1 text-2xl text-muted">/ {o.limit}</span>
                  ) : null}
                </p>
              </div>
              <PlusOneButton onPress={() => void log()} />
            </div>
            {status ? <p className="mt-2 text-sm text-fg">{status}</p> : null}
            <p className="text-sm text-muted">
              {o.lastSmokeAt && o.minutesSince !== null
                ? copy.lastOne(durationText(o.minutesSince))
                : copy.noneYet}
              {o.hasPrice ? (
                <>
                  {' · '}
                  {copy.spentToday} <Money minor={o.spentToday} animate={false} />
                </>
              ) : null}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" className={PILL_BTN} onClick={() => setTotalOpen(true)}>
                {copy.editTotal}
              </button>
              <button type="button" className={PILL_BTN} onClick={() => setCravingOpen(true)}>
                {copy.craving}
              </button>
            </div>
          </section>

          {o.phase === 'smoke_free' && o.minutesSince !== null ? (
            <Milestones minutesSince={o.minutesSince} />
          ) : null}
        </div>

        {/* Full width on desktop, so the bento grid's columns aren't squeezed into half the page. */}
        <div className="lg:col-span-2">
          <BentoGrid
            tiles={visibleTiles(rows ?? [], usable, { includeHidden: false })}
            label={copy.moduleName}
          />
        </div>

        <section
          aria-label={copy.progress}
          className={`${CARD} flex flex-col gap-3 lg:col-start-2 lg:row-start-1`}
        >
          <h2 className={LABEL}>{copy.progress}</h2>
          <TaperChart days={o.taperDays} today={o.today} quitDate={o.quitDate} />
          <ul className="flex flex-col gap-1 text-sm">
            <li className="text-fg">
              {o.quitDate ? copy.quitDate(shortDate(o.quitDate)) : copy.cuttingTo(o.floor ?? 0)}
            </li>
            {o.nextStep ? (
              <li className="text-muted">
                {copy.nextStep(shortDate(o.nextStep.date), o.nextStep.limit)}
              </li>
            ) : null}
            {o.weekAverage !== null ? (
              <li className="text-muted">
                {copy.weekAverage(o.weekAverage.toLocaleString('en-US'))}
              </li>
            ) : null}
          </ul>
        </section>

        <div className="flex flex-col gap-4">
          <section aria-label={copy.week} className={CARD}>
            <h2 className={LABEL}>{copy.week}</h2>
            <div className="mt-3">
              <CountChart days={o.week} today={o.today} />
            </div>
          </section>

          {o.hasPrice ? (
            <section aria-label={copy.money} className={CARD}>
              <h2 className={LABEL}>{copy.money}</h2>
              <dl className="mt-2 grid grid-cols-3 gap-2 text-sm">
                <div>
                  <dt className="text-muted">{copy.spentToday}</dt>
                  <dd>
                    <Money minor={o.spentToday} animate={false} />
                  </dd>
                </div>
                <div>
                  <dt className="text-muted">{copy.thisWeek}</dt>
                  <dd>
                    <Money minor={o.spentWeek} animate={false} />
                  </dd>
                </div>
                <div>
                  <dt className="text-muted">{copy.allTime}</dt>
                  <dd>
                    <Money minor={o.spentAll} animate={false} />
                  </dd>
                </div>
              </dl>
              <p className="mt-2 text-sm text-muted">
                {o.saved >= 0
                  ? copy.savedLine(formatPeso(o.saved))
                  : copy.overLine(formatPeso(-o.saved))}
              </p>
            </section>
          ) : (
            <p className="text-sm text-muted">{copy.noPrice}</p>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <MenuCard
            label={copy.pages}
            items={[
              {
                to: `${QUIT_BASE}/history`,
                label: copy.history,
                icon: 'history',
                value:
                  o.weekAverage !== null
                    ? copy.menuAverage(o.weekAverage.toLocaleString('en-US'))
                    : null,
              },
              {
                to: `${QUIT_BASE}/triggers`,
                label: copy.triggers,
                icon: 'bolt',
                value: o.resistedWeek > 0 ? copy.menuResisted(o.resistedWeek) : null,
              },
            ]}
          />

          <SupportCard
            country={plan.quitlineCountry}
            onCountryChange={(c) =>
              void savePlan(env, draftFromPlan(plan, o.today, { quitlineCountry: c }))
            }
          />

          <div className={`${CARD} flex items-center justify-between gap-3`}>
            <div>
              <p className="text-fg">{copy.showOnToday}</p>
              <p className="text-sm text-muted">{copy.showOnTodayHint}</p>
            </div>
            <Switch
              label={copy.showOnToday}
              checked={onToday}
              onCheckedChange={(v) => void setQuitOnToday(env, v)}
            />
          </div>
        </div>
      </div>

      <DayTotalSheet
        open={totalOpen}
        onOpenChange={setTotalOpen}
        title={copy.todaysTotal}
        initial={o.count}
        onSave={async (n) => {
          const r = await setDayTotal(env, o.today, n);
          return r.ok ? null : errorText(r.error);
        }}
      />
      <CravingSheet
        open={cravingOpen}
        onOpenChange={(open) => (open ? setCravingOpen(true) : closeCraving())}
        until={delay.until}
        now={tick.getTime()}
        onStartDelay={delay.start}
        onResisted={async (trigger, delayUsed) => {
          await logCraving(env, { resisted: true, trigger, delayUsed });
          closeCraving();
          showToast({ message: copy.cravingPassed(o.resistedWeek + 1) });
        }}
        onSmoked={async (trigger, delayUsed) => {
          await logCraving(env, { resisted: false, trigger, delayUsed });
          closeCraving();
          await log(trigger);
        }}
      />
    </main>
  );
}

const ACCENT_PILL =
  'inline-flex min-h-11 items-center justify-center rounded-full bg-accent px-5 font-medium text-on-accent hover:brightness-110 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[var(--ring)]';
